import { normalize } from 'normalizr'
import { Commit } from 'vuex'

import CustomerNotification, {
  HEADER_PER_PAGE_NOTIFICATIONS_AMOUNT,
  NOTIFICATION_PAGE_PER_PAGE_NOTIFICATIONS_AMOUNT,
} from '@/models/customer-notification'

import axios, { AxiosModule } from '@/axios-module'
import {
  customerNotificationsSchema,
  NotificationsResponse,
  NotificationResponse,
} from '@/schemas/customer-notification'
export enum CustomerNotificationsActionTypes {
  Load = '[CustomerNotifications] Load',
  HeaderLoad = '[CustomerNotifications] HeaderLoad',
  Dismiss = '[CustomerNotifications] Dismiss',
  MarkAsSeen = '[CustomerNotifications] MarkAsSeen',
  MarkAsReviewed = '[CustomerNotifications] MarkAsReviewed ',
}

export enum CustomerNotificationsMutationTypes {
  LoadStarted = '[CustomerNotifications] LoadStarted',
  LoadSuccess = '[CustomerNotifications] LoadSuccess',
  SetHasMore = '[CustomerNotifications] SetHasMore',
  LoadBookingNotificationSuccess = '[CustomerNotifications] LoadBookingNotificationSuccess',
  HeaderLoadSuccess = '[CustomerNotifications] HeaderLoadSuccess',
  UpdateSuccess = '[CustomerNotifications] UpdateSuccess',
  ChangeBookingId = '[CustomerNotifications] ChangeBookingId',
  GetSuccess = '[CustomerNotifications] GetSuccess',
  Reset = '[CustomerNotifications] Reset',
}

export enum CustomerNotificationsGetters {
  HeaderCustomerNotifications = '[CustomerNotifications] HeaderCustomerNotifications',
  CustomerNotifications = '[CustomerNotifications] CustomerNotifications',
  CustomerNotificationsByBookingId = '[CustomerNotifications] CustomerNotificationsByBookingId',
  GetNotificationById = '[CustomerNotifications] GetNotificationById',
  HasMore = '[CustomerNotifications] HasMore',
}

export interface State {
  customerNotificationsLoaded: boolean
  customerNotificationIds: number[]
  customerHeaderNotificationIds: number[]
  customerNotificationIdsByBooking: { [id: number]: number[] }
  customerNotificationEntities: {
    [id: number]: CustomerNotification
  }
  bookingId: number | null
  hasMore: boolean
}

function initialState(): State {
  return {
    customerNotificationsLoaded: false,
    customerNotificationIds: [],
    customerHeaderNotificationIds: [],
    customerNotificationEntities: {},
    customerNotificationIdsByBooking: {},
    bookingId: null,
    hasMore: false,
  }
}

export default {
  state: initialState,
  getters: {
    [CustomerNotificationsGetters.HeaderCustomerNotifications](
      state: State,
    ): CustomerNotification[] {
      return state.customerHeaderNotificationIds.map(
        (id) => state.customerNotificationEntities[id],
      )
    },
    [CustomerNotificationsGetters.CustomerNotificationsByBookingId](
      state: State,
    ): (bookingId: number) => CustomerNotification[] {
      return (bookingId: number) =>
        (state.customerNotificationIdsByBooking[bookingId] || []).map(
          (id) => state.customerNotificationEntities[id],
        )
    },
    [CustomerNotificationsGetters.CustomerNotifications](
      state: State,
    ): CustomerNotification[] {
      return state.customerNotificationIds.map(
        (id) => state.customerNotificationEntities[id],
      )
    },
    [CustomerNotificationsGetters.GetNotificationById](
      state: State,
    ): (id: number) => CustomerNotification {
      return (id: number) => state.customerNotificationEntities[id]
    },
    [CustomerNotificationsGetters.HasMore](state: State): boolean {
      return state.hasMore
    },
  },
  actions: {
    async [CustomerNotificationsActionTypes.HeaderLoad]({
      commit,
    }: {
      commit: Commit
    }) {
      const params = {
        page: 1,
        per_page: HEADER_PER_PAGE_NOTIFICATIONS_AMOUNT,
      }
      const response = await axios.get('/customer/notifications', { params })
      const normalizedResponse = normalize(
        response.data.entities,
        customerNotificationsSchema,
      )
      commit(
        CustomerNotificationsMutationTypes.HeaderLoadSuccess,
        normalizedResponse,
      )
      commit(CustomerNotificationsMutationTypes.LoadSuccess, normalizedResponse)
    },
    async [CustomerNotificationsActionTypes.Load](
      { commit }: { commit: Commit },
      {
        page,
        perPage,
        bookingId,
      }: { page: number; perPage: number; bookingId: number | null } = {
        page: 1,
        perPage: NOTIFICATION_PAGE_PER_PAGE_NOTIFICATIONS_AMOUNT,
        bookingId: null,
      },
    ) {
      const params = { page, per_page: perPage, enquiry_id: bookingId }
      commit(CustomerNotificationsMutationTypes.LoadStarted)
      const response = await axios.get('/customer/details_notifications', {
        params,
      })
      const normalizedResponse = normalize(
        response.data.entities,
        customerNotificationsSchema,
      )

      commit(CustomerNotificationsMutationTypes.LoadSuccess, normalizedResponse)
      commit(
        CustomerNotificationsMutationTypes.SetHasMore,
        response.data.meta.hasMore,
      )
      if (bookingId) {
        commit(
          CustomerNotificationsMutationTypes.LoadBookingNotificationSuccess,
          { bookingId, normalizedResponse },
        )
      }
    },
    async [CustomerNotificationsActionTypes.Dismiss](
      { commit }: { commit: Commit },
      id: number,
    ) {
      const params = { dismissed: true }
      commit(CustomerNotificationsMutationTypes.UpdateSuccess, { id, params })
      await axios.patch(`/customer/notifications/${id}`, { dismissed: true })
    },
    async [CustomerNotificationsActionTypes.MarkAsSeen](
      { commit }: { commit: Commit },
      ids: number[],
    ) {
      const params = { seen: true }
      ids.forEach((id: number) =>
        commit(CustomerNotificationsMutationTypes.UpdateSuccess, {
          id,
          params,
        }),
      )
      await AxiosModule.post('/customer/read_notifications/', { ids })
    },
    async [CustomerNotificationsActionTypes.MarkAsReviewed](
      { commit }: { commit: Commit },
      ids: number[],
    ) {
      const params = { isReviewed: true }
      ids.forEach((id: number) =>
        commit(CustomerNotificationsMutationTypes.UpdateSuccess, {
          id,
          params,
        }),
      )
      await AxiosModule.post('/customer/reviewed_notifications/', { ids })
    },
  },
  mutations: {
    [CustomerNotificationsMutationTypes.LoadStarted](state: State) {
      state.customerNotificationsLoaded = false
    },
    [CustomerNotificationsMutationTypes.LoadSuccess](
      state: State,
      normalizedResponse: NotificationsResponse,
    ) {
      state.customerNotificationEntities = {
        ...state.customerNotificationEntities,
        ...normalizedResponse.entities.customerNotifications,
      }

      normalizedResponse.result.forEach((id) => {
        if (state.customerNotificationIds.indexOf(id) < 0) {
          state.customerNotificationIds = [...state.customerNotificationIds, id]
        }
      })

      state.customerNotificationsLoaded = true
    },
    [CustomerNotificationsMutationTypes.LoadBookingNotificationSuccess](
      state: State,
      {
        bookingId,
        normalizedResponse,
      }: { bookingId: number; normalizedResponse: NotificationsResponse },
    ) {
      const idsForBooking =
        state.customerNotificationIdsByBooking[bookingId] || []

      normalizedResponse.result.forEach((id) => {
        if (!idsForBooking.includes(id)) {
          idsForBooking.push(id)
        }
      })

      state.customerNotificationIdsByBooking = {
        ...state.customerNotificationIdsByBooking,
        [bookingId]: idsForBooking,
      }
    },
    [CustomerNotificationsMutationTypes.HeaderLoadSuccess](
      state: State,
      normalizedResponse: NotificationsResponse,
    ) {
      state.customerHeaderNotificationIds = normalizedResponse.result
    },
    [CustomerNotificationsMutationTypes.GetSuccess](
      state: State,
      normalizedResponse: NotificationResponse,
    ) {
      const id = normalizedResponse.result

      state.customerNotificationEntities = {
        ...state.customerNotificationEntities,
        ...normalizedResponse.entities.customerNotifications,
      }
      if (!state.customerNotificationIds.includes(id)) {
        state.customerNotificationIds = [...state.customerNotificationIds, id]
      }
    },
    [CustomerNotificationsMutationTypes.SetHasMore](
      state: State,
      hasMore: boolean,
    ) {
      state.hasMore = hasMore
    },
    [CustomerNotificationsMutationTypes.UpdateSuccess](
      state: State,
      { id, params }: { id: number; params: any },
    ) {
      let notification = state.customerNotificationEntities[id]
      notification = Object.assign({}, notification, params)

      state.customerNotificationEntities = {
        ...state.customerNotificationEntities,
        [id]: notification,
      }
    },
    [CustomerNotificationsMutationTypes.ChangeBookingId](
      state: State,
      bookingId: number,
    ) {
      state.bookingId = bookingId
    },
    [CustomerNotificationsMutationTypes.Reset](state: State) {
      Object.assign(state, initialState())
    },
  },
}
